// bs02 Beshik to'yi — gentle baby-cradle ceremony catalog card.
//
// Paper-invitation feel: outer soft amber double-hairline mat on a creamy
// paper base, inner ivory card with a centred beshik (cradle) medallion
// rendered as inline SVG, parents' names in Fraunces, and a small-caps
// caption with date and venue.
import { Photo, type LayoutCtx } from "./_shared";

export function BeshikToyiPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  // Warm cream mat — gentle amber wash for the cradle theme.
  const mat = `color-mix(in srgb, ${bg} 75%, ${accent} 22%)`;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: mat,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          position: "relative",
          boxShadow: `inset 0 0 0 1px ${accent}55, inset 0 0 0 6px ${bg}, inset 0 0 0 7px ${accent}33`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          BESHIK TO'YI
        </div>

        {/* Cradle medallion */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
            borderRadius: "50%",
            background: `radial-gradient(circle at 50% 45%, ${accent}1C, ${bg} 70%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `inset 0 0 0 1px ${accent}55`,
            overflow: "hidden",
          }}
        >
          <Photo
            src={photo}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", borderRadius: "50%" }}
            fallback={
              <svg viewBox="0 0 100 100" style={{ width: "75%", height: "75%" }} aria-hidden>
                {/* Beshik (cradle) silhouette: bed + rocker arches + canopy */}
                {/* Canopy arch */}
                <path
                  d="M22 38 Q50 22 78 38"
                  stroke={accent}
                  strokeWidth="1.4"
                  fill="none"
                />
                {/* Canopy struts */}
                <line x1="50" y1="22" x2="50" y2="32" stroke={accent} strokeWidth="0.8" />
                <circle cx="50" cy="20" r="1.6" fill={accent} />
                {/* Cradle bed */}
                <rect x="22" y="50" width="56" height="16" rx="2" fill={accent} opacity="0.85" />
                <rect x="22" y="50" width="56" height="3" fill={accent} />
                {/* Rocker arches */}
                <path d="M16 66 Q50 80 84 66" stroke={accent} strokeWidth="1.4" fill="none" />
                <path d="M22 66 Q50 76 78 66" stroke={accent} strokeWidth="0.8" fill="none" opacity="0.7" />
                {/* Tiny stars above canopy */}
                {[
                  [34, 30], [50, 26], [66, 30],
                ].map(([x, y], i) => (
                  <circle key={i} cx={x} cy={y} r="0.9" fill={accent} opacity="0.7" />
                ))}
              </svg>
            }
          />
        </div>

        <div
          style={{
            fontFamily: font,
            fontSize: 28,
            lineHeight: 1.1,
            fontWeight: 500,
            color: ink,
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          BESHIK TO'YI · {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
